/**
 * What the pages say (SYR-111 v4).
 *
 * Every page the models read in at least two answers was fetched and read by
 * one model with search off, against the brand's statements: a score from 1
 * to 7 from the page alone, and the page's own words it rests on. This shows
 * the most-read pages: what each says about the brand, the most-read pages
 * that never name it, and the ones we could not read.
 */

import React from 'react';
import { useIntl } from 'react-intl';
import { Tag, Tooltip } from 'antd';
import { Card, Eyebrow } from '../../atoms';
import { THEME } from '../../config/theme';
import {
  IModelBeliefsPageFinding,
  IModelBeliefsPageFindings,
} from '../../../../services/nestjs-base-api';
import { InfoTip } from './InfoTip';
import { ANCHORS } from './beliefCells';
import { useMarketLabel, usePageLabel } from './pageKind';

interface Props {
  findings: IModelBeliefsPageFindings;
  // Statement wording by code, so a reading is shown against its statement.
  statementText: Record<string, string>;
  brandName: string;
}

export const WhatPagesSay: React.FunctionComponent<Props> = ({ findings, statementText, brandName }) => {
  const intl = useIntl();
  const marketLabel = useMarketLabel();
  const pageLabel = usePageLabel();

  const readBy = (page: IModelBeliefsPageFinding) =>
    intl.formatMessage(
      {
        id: 'marketerView.modelBeliefs.pagesSay.readIn',
        defaultMessage: 'read in {n} answers',
      },
      { n: page.answers },
    ) + (page.models.length > 0 ? ` (${page.models.map((m) => `${m.label} ${m.answers}`).join(', ')})` : '');

  const neverNamed = findings.silent.filter((p) =>
    p.gap !== undefined ? p.gap : p.named === false && !p.site,
  );
  const otherMarket = [...findings.speaking, ...findings.silent].filter((p) => p.market);
  // One entry per site: two unreadable reddit threads are one gap.
  const unreadable = Object.entries(
    findings.unreadable.reduce<Record<string, number>>((acc, p) => {
      acc[p.domain] = (acc[p.domain] ?? 0) + p.answers;
      return acc;
    }, {}),
  ).sort((a, b) => b[1] - a[1]);
  const inPassing = findings.silent.filter((p) => p.named === true);

  const quietList = (pages: IModelBeliefsPageFinding[]) => (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
      {pages.map((p) => (
        <Tooltip key={p.url} title={`${pageLabel(p.kind, p.site)} · ${readBy(p)}`}>
          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 12.5,
              color: THEME.textBase,
              background: THEME.bgChip,
              border: `1px solid ${THEME.border}`,
              borderRadius: 999,
              padding: '3px 10px',
            }}
          >
            {p.domain} · {p.answers}
          </a>
        </Tooltip>
      ))}
    </div>
  );

  return (
    <Card>
      <Eyebrow style={{ marginBottom: 6 }}>
        <InfoTip
          title={intl.formatMessage({
            id: 'marketerView.modelBeliefs.pagesSay.tip',
            defaultMessage:
              'Each page the models read in at least two answers was fetched and read once, with search off, against your statements. The score is how strongly the page itself supports the statement, from 1 to 7, and the quote is the page’s own words it rests on. A page that says nothing on a statement is left out of that statement rather than scored.',
          })}
        >
          {intl.formatMessage({
            id: 'marketerView.modelBeliefs.pagesSay.title',
            defaultMessage: 'What the most-read pages say about you',
          })}
        </InfoTip>
      </Eyebrow>
      <div style={{ fontSize: 13, color: THEME.textSecondary, marginBottom: 14 }}>
        {intl.formatMessage(
          {
            id: 'marketerView.modelBeliefs.pagesSay.count',
            defaultMessage: '{read, plural, one {One page was read} other {{read} pages were read}}. The most-read pages that speak about {brand}:',
          },
          { read: findings.pagesRead, brand: brandName },
        )}
      </div>

      <div style={{ display: 'grid', gap: 14 }}>
        {findings.speaking.map((page) => (
          <div
            key={page.url}
            style={{ border: `1px solid ${THEME.border}`, borderRadius: 12, padding: '12px 16px', minWidth: 0 }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 8, marginBottom: 8 }}>
              <a
                href={page.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 700, color: THEME.textBase, fontSize: 14 }}
              >
                {page.domain}
              </a>
              <Tag style={{ margin: 0, fontSize: 11 }}>{pageLabel(page.kind, page.site)}</Tag>
              {page.market && (
                <Tag color="orange" style={{ margin: 0, fontSize: 11 }}>
                  {marketLabel(page.market)}
                </Tag>
              )}
              <span style={{ fontSize: 12, color: THEME.textMuted }}>
                {readBy(page)} ·{' '}
                {intl.formatMessage(
                  {
                    id: 'marketerView.modelBeliefs.pagesSay.speaksTo',
                    defaultMessage: 'speaks to {covered} of {asked} statements',
                  },
                  { covered: page.covered, asked: page.asked },
                )}
              </span>
            </div>
            {page.title && (
              <div
                title={page.title}
                style={{
                  fontSize: 12.5,
                  color: THEME.textSecondary,
                  marginTop: -4,
                  marginBottom: 8,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {page.title}
              </div>
            )}
            <div style={{ display: 'grid', gap: 8 }}>
              {page.readings.map((reading) => (
                <div key={reading.code} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', minWidth: 0 }}>
                  <Tooltip title={ANCHORS[Math.round(reading.score)] ?? ''}>
                    <span
                      style={{
                        flex: '0 0 auto',
                        minWidth: 26,
                        textAlign: 'center',
                        fontWeight: 700,
                        fontSize: 13,
                        borderRadius: 6,
                        padding: '1px 6px',
                        background: THEME.bgChip,
                        color: THEME.textBase,
                      }}
                    >
                      {reading.score}
                    </span>
                  </Tooltip>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, color: THEME.textSecondary }}>
                      {statementText[reading.code] ?? reading.code}
                    </div>
                    <div style={{ fontSize: 13.5, color: THEME.textBase, fontStyle: 'italic', lineHeight: 1.5 }}>
                      “{reading.quote}”
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      {neverNamed.length > 0 && (
        <div style={{ marginTop: 18 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: THEME.textBase, marginBottom: 4 }}>
            {intl.formatMessage(
              {
                id: 'marketerView.modelBeliefs.pagesSay.neverNamed',
                defaultMessage: 'Read often, and {brand} is not on them',
              },
              { brand: brandName },
            )}
          </div>
          <div style={{ fontSize: 13, color: THEME.textSecondary, marginBottom: 8 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.pagesSay.neverNamedBody',
              defaultMessage:
                'The models keep returning to these pages and they never name you. The number is how many answers read each one.',
            })}
          </div>
          {quietList(neverNamed)}
        </div>
      )}

      {inPassing.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: THEME.textBase, marginBottom: 4 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.pagesSay.inPassing',
              defaultMessage: 'Named in passing only',
            })}
          </div>
          <div style={{ fontSize: 13, color: THEME.textSecondary, marginBottom: 8 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.pagesSay.inPassingBody',
              defaultMessage: 'These pages mention you but say nothing about you on any statement.',
            })}
          </div>
          {quietList(inPassing)}
        </div>
      )}

      {otherMarket.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <div style={{ fontSize: 13.5, fontWeight: 700, color: THEME.textBase, marginBottom: 4 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.pagesSay.otherMarket',
              defaultMessage: 'From another market, not scored',
            })}
          </div>
          <div style={{ fontSize: 13, color: THEME.textSecondary, marginBottom: 8 }}>
            {intl.formatMessage({
              id: 'marketerView.modelBeliefs.pagesSay.otherMarketBody',
              defaultMessage:
                'The models read these pages, but they are about your store in another country or sit on another country\u2019s site, so their prices, delivery and availability are not this market\u2019s. They are kept out of the scores and the record fixes.',
            })}
          </div>
          {quietList(otherMarket)}
        </div>
      )}

      {unreadable.length > 0 && (
        <div style={{ marginTop: 16, fontSize: 12.5, color: THEME.textMuted }}>
          {intl.formatMessage(
            {
              id: 'marketerView.modelBeliefs.pagesSay.unreadable',
              defaultMessage: 'We could not read {pages}, so what they say is unknown.',
            },
            {
              pages: unreadable.map(([domain, answers]) => `${domain} (${answers})`).join(', '),
            },
          )}
        </div>
      )}
    </Card>
  );
};
